import { formatMoney } from "@ondo/shared";

/**
 * A figure on a settlement screen, or a placeholder where there is no figure.
 *
 * `formatMoney` renders a Money; this adds the two things a settlement needs on
 * top of it, and it needed them in two places.
 *
 * **A null is a dash, never £0.** A share nobody has computed is not a share
 * that came to nothing, and printing zero would announce that a resale made no
 * profit before anybody has said what it made. The dash is the screen's own
 * copy, so it is passed in rather than baked here — `SETTLEMENT_COPY.pending`
 * and `ARTIST_SETTLEMENT_COPY.pending` are the same character today and are
 * free to stop being.
 *
 * **A negative is signed with U+2212, not a hyphen.** A loss on a resale is a
 * real state — see the loss case on the gallery's settlements screen — and a
 * hyphen-minus in a proportional face reads as punctuation next to digits.
 *
 * Lifted from `gallery/settlements.model.ts` and
 * `collector/artistSettlements.model.ts`, which held byte-identical copies. Two
 * copies of a rounding-and-sign rule about money is exactly the kind of pair
 * that drifts silently and is discovered from a screenshot.
 */
export function money(minor: number | null, currency: string, pending: string): string {
  if (minor === null) return pending;

  const formatted = formatMoney({ minor: Math.abs(minor), currency });

  return minor < 0 ? `−${formatted}` : formatted;
}

/**
 * A typed figure to minor units, or null for anything that is not one.
 *
 * Lifted from `gallery/sellWizard.model.ts` when the estate's forms became its
 * second caller — a parsing rule about money is exactly the kind of copy that
 * drifts. The decimals are counted on the STRING, not the parsed float: 1.005
 * is not exactly representable, so asking the number how many decimals it had
 * would answer about the binary rather than about the typing.
 */
export function parseMoneyMinor(typed: string): number | null {
  const cleaned = typed.replace(/[\s,_]/g, "").replace(/^[^\d.-]+/, "");
  if (cleaned === "" || !/^-?\d*\.?\d*$/.test(cleaned)) return null;

  const dot = cleaned.indexOf(".");
  if (dot !== -1 && cleaned.length - dot - 1 > 2) return null;

  const major = Number(cleaned);
  if (!Number.isFinite(major) || major <= 0) return null;

  return Math.round(major * 100);
}
